/**
 * Which shell the app is running in.
 *
 * One bundle serves three ways — a browser tab, an installed web app, and the
 * Tauri desktop app (`src-tauri/`, `docs/DESKTOP.md`) — and only a handful of
 * things differ between them: where a sync folder is written, whether a menu
 * bar exists, how an update arrives. Those few places ask here rather than
 * sniffing for themselves, and everything Tauri-specific is imported
 * dynamically behind `isTauri()`, so a browser never loads it.
 */

/** Tauri v2 injects this before any page script runs. */
export function isTauri(): boolean {
  return typeof globalThis === 'object' && '__TAURI_INTERNALS__' in globalThis;
}

/** Installed to the Home Screen, the Dock or as a desktop web app, rather than open in a tab. */
export function isStandaloneWebApp(): boolean {
  const nav = globalThis.navigator as (Navigator & { standalone?: boolean }) | undefined;
  return (
    nav?.standalone === true ||
    (typeof globalThis.matchMedia === 'function' &&
      globalThis.matchMedia('(display-mode: standalone)').matches)
  );
}

/** Apple platforms show ⌘ and ⌥; everything else shows Ctrl and Alt. */
export function isApplePlatform(): boolean {
  const nav = globalThis.navigator as Navigator | undefined;
  return /Mac|iPhone|iPad|iPod/.test(nav?.platform ?? nav?.userAgent ?? '');
}

/**
 * A short default name for this device, used to label its file in a sync
 * folder until the pilot names it. Coarse on purpose: the user agent is all
 * there is, and "Mac" is more useful than a guessed model.
 */
export function defaultDeviceName(userAgent: string = globalThis.navigator.userAgent): string {
  if (userAgent.includes('iPhone')) return 'iPhone';
  if (userAgent.includes('iPad')) return 'iPad';
  if (userAgent.includes('Android')) return 'Android';
  if (/Macintosh|Mac OS X/.test(userAgent)) return 'Mac';
  if (userAgent.includes('Windows')) return 'Windows';
  if (userAgent.includes('Linux')) return 'Linux';
  return 'Browser';
}
